import type React from "react";
import { useContext } from "react";

import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addMinutes } from "date-fns";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { getCareEventsSuggestedQueryKey } from "api/hooks/useGetCareEventsSuggested";
import { useGetTreatmentActivityLogCategories } from "api/hooks/useGetTreatmentActivityLogCategories";
import { usePostTreatmentActivityLog } from "api/hooks/usePostTreatmentActivityLog";
import { usePutTreatmentActivityLog } from "api/hooks/usePutTreatmentActivityLog";
import type {
  LogEntry,
  TreatmentActivityLogCategory,
  TreatmentActivityLogPayload,
} from "api/schemas/TreatmentActivityLog";
import { Form, Input } from "routes/calendar/components/Forms/styles";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUIDropdown, MUITextInput } from "shared/atoms/inputs";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";

import { FormState } from "../PatientHeader/PatientHeaderInfo/PatientDataList/TreatmentSwitch/FormState";

const timeout = 2000;

type FormData = {
  category: TreatmentActivityLogCategory;
  duration: number;
  start_date: string;
};

interface Props {
  selectedActivityLog: LogEntry | null;
  onClose: () => void;
  refetch: () => void;
}

export const ActivityLogForm: React.FC<Props> = ({ selectedActivityLog, onClose, refetch }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { patient } = useContext(CurrentPatientContext);
  const { format: formatDate, parseISO: parseISODate, formats } = useLocalizedDate();

  const { data: activityCategoriesData } = useGetTreatmentActivityLogCategories();
  const postTreatmentActivityLog = usePostTreatmentActivityLog();
  const putTreatmentActivityLog = usePutTreatmentActivityLog();

  const activityCategories =
    activityCategoriesData?.map(category => ({ label: category.description, value: category.name })) || [];

  const form = useForm<FormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      category: selectedActivityLog ? selectedActivityLog.category.name : undefined,
      duration: selectedActivityLog ? selectedActivityLog.duration / 60 : undefined,
      start_date: selectedActivityLog
        ? formatDate(parseISODate(selectedActivityLog.start_time), formats.ISO_DATE)
        : formatDate(new Date(), formats.ISO_DATE),
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSuccess = () => {
    refetch();
    queryClient.invalidateQueries(getCareEventsSuggestedQueryKey([patient.id]));
    setTimeout(onClose, timeout);
  };

  const onSubmit = handleSubmit((formData: FormData) => {
    try {
      // Parse date string and create Date at midnight UTC to avoid timezone issues
      const [year, month, day] = formData.start_date.split("-").map(Number);
      const startTime = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
      const endTime = addMinutes(startTime, formData.duration);

      if (selectedActivityLog) {
        putTreatmentActivityLog.mutateAsync(
          {
            id: selectedActivityLog.id,
            data: {
              category: formData.category,
              start_time: startTime.toISOString(),
              end_time: endTime.toISOString(),
            },
          },
          {
            onSuccess: () => {
              onSuccess();
            },
          }
        );
      } else {
        const data: TreatmentActivityLogPayload = {
          category: formData.category,
          patient_id: patient.id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
        };

        postTreatmentActivityLog.mutateAsync(
          { data },
          {
            onSuccess: () => {
              onSuccess();
            },
          }
        );
      }
    } catch (e) {
      if (e instanceof Error || typeof e === "string") {
        reportError("ActivityLogForm.tsx", e);
      }
    }
  });

  return (
    <MUIUnifiedModal
      open
      onClose={onClose}
      width="390px"
      title={selectedActivityLog ? t("patients.activity_log.form.edit_header") : t("patients.activity_log.form.header")}
    >
      <Box p={3}>
        <FormProvider {...form}>
          <Form onSubmit={onSubmit} noValidate>
            <Input>
              <MUIDropdown
                label={t("patients.activity_log.form.category")}
                name="category"
                options={activityCategories}
                required
                error={errors.category && t("errors.field.required")}
              />
            </Input>
            <Input>
              <Controller
                name="start_date"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label={t("patients.activity_log.form.start_time")}
                    format={formats.ISO_DATE}
                    value={value ? parseISODate(value) : null}
                    onChange={input => {
                      if (input) {
                        onChange(formatDate(input, formats.ISO_DATE));
                      } else {
                        onChange(input);
                      }
                    }}
                    slotProps={{
                      textField: {
                        error: !!errors.start_date,
                        helperText: errors.start_date && t("errors.field.required"),
                        fullWidth: true,
                      },
                    }}
                  />
                )}
              />
            </Input>
            <Input>
              <MUITextInput
                label={t("patients.activity_log.form.duration")}
                name="duration"
                type="number"
                min={1}
                max={60}
                required
                error={(() => {
                  if (errors.duration?.type === "required") return t("errors.field.required", "");
                  if (errors.duration?.type === "min") return t("errors.field.min", { min: 1 }) as string;
                  if (errors.duration?.type === "max") return t("errors.field.max", { max: 60 }) as string;
                  return undefined;
                })()}
              />
            </Input>
            <BaseButton
              text="Save"
              type="submit"
              disabled={
                postTreatmentActivityLog.isLoading ||
                postTreatmentActivityLog.isSuccess ||
                putTreatmentActivityLog.isLoading ||
                putTreatmentActivityLog.isSuccess
              }
              style={{ marginBottom: "16px" }}
            />
          </Form>
        </FormProvider>

        <FormState
          formState={postTreatmentActivityLog.status}
          messages={{ loading: t("patients.activity_log.form.loading") }}
        />

        <FormState
          formState={putTreatmentActivityLog.status}
          messages={{ loading: t("patients.activity_log.form.loading") }}
        />
      </Box>
    </MUIUnifiedModal>
  );
};
